import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Package } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SERVICES } from "@/components/Services";
import { BUSINESS } from "@/constants/business";

const TIME_SLOTS = [
    "10:00 AM - 12:00 PM",
    "12:00 PM - 02:00 PM",
    "02:00 PM - 04:00 PM",
];

const UPSELL_SERVICES = ["Oil Change & Fluids", "Standard Vehicle Service"];
const BUNDLE_NAME = "Full Cleanse Package";

const EMPTY = {
    customer_name: "",
    phone: "",
    email: "",
    vehicle: "",
    service: "",
    date: "",
    time_slot: TIME_SLOTS[0],
    address: "",
    notes: "",
    addon: "",
};

const inputCls =
    "w-full bg-[#0B0C0E] border border-[#383E4C] rounded-xl px-4 py-3.5 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-amber-500 transition-colors duration-200";

const SERVICE_OPTIONS = SERVICES.flatMap((s) =>
    s.tiers
        ? s.tiers.map((t) => ({
              value: `${s.name} — ${t.name}`,
              label: `${s.name} — ${t.name} ($${t.price} + parts, ~${t.duration.toLowerCase()})`,
          }))
        : [{ value: s.name, label: `${s.name}${s.duration ? ` — ${s.duration.toLowerCase()}` : ""}` }]
);

export default function BookingForm({ preselectedService }) {
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [confirmation, setConfirmation] = useState(null);

    useEffect(() => {
        if (preselectedService) {
            setForm((f) => ({ ...f, service: preselectedService, addon: "" }));
            document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
        }
    }, [preselectedService]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const showUpsell = UPSELL_SERVICES.includes(form.service);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const payload = { ...form, addon: showUpsell ? form.addon : "" };
            const { data } = await api.post("/bookings", payload);
            setConfirmation(data);
            setForm(EMPTY);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="booking" data-testid="booking-section" className="py-24 sm:py-32 bg-[#0B0C0E] border-y border-[#22262F]">
            <div className="max-w-4xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-12"
                >
                    <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400 font-semibold mb-4">
                        04 / Request Dispatch
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white leading-tight">
                        Book your mechanic
                    </h2>
                    <p className="mt-4 text-base text-zinc-400 max-w-xl">
                        Tell us what your vehicle needs and where it is. Dispatch confirms your slot within 15 minutes
                        during business hours ({BUSINESS.hoursShort}).
                    </p>
                    <p className="mt-2 text-xs font-mono uppercase tracking-[0.18em] text-zinc-500" data-testid="booking-labour-note">
                        {BUSINESS.pricingNote}
                    </p>
                </motion.div>

                {confirmation ? (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-10"
                        data-testid="booking-confirmation"
                    >
                        <CheckCircle2 className="w-10 h-10 text-amber-400 mb-6" />
                        <h3 className="font-display text-3xl font-bold uppercase text-white">
                            Dispatch request received
                        </h3>
                        <p className="mt-3 text-zinc-300 text-sm leading-relaxed">
                            Reference <span className="font-mono text-amber-400">{confirmation.id}</span> —{" "}
                            {confirmation.service}
                            {confirmation.addon ? ` with the ${confirmation.addon}` : ""} for your{" "}
                            {confirmation.vehicle} on {confirmation.date}, {confirmation.time_slot}. Our dispatcher
                            will call {confirmation.phone} within 15 minutes to confirm, and a confirmation email is
                            on its way to you.
                        </p>
                        <div className="mt-6 flex flex-wrap items-center gap-6">
                            <Link
                                to={`/track?ref=${confirmation.id}`}
                                data-testid="booking-track-link"
                                className="inline-flex items-center gap-2 bg-amber-500 text-[#0B0C0E] text-xs font-bold uppercase tracking-widest rounded-full px-5 py-2.5 hover:bg-amber-400 transition-colors duration-200"
                            >
                                Track this booking
                            </Link>
                            <button
                                onClick={() => setConfirmation(null)}
                                data-testid="booking-new-request-button"
                                className="text-xs font-mono uppercase tracking-[0.2em] text-amber-400 hover:text-amber-300 transition-colors duration-200"
                            >
                                + Submit another request
                            </button>
                        </div>
                    </motion.div>
                ) : (
                    <motion.form
                        onSubmit={submit}
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                        className="rounded-2xl border border-[#22262F] bg-[#121418] p-8 sm:p-10 grid sm:grid-cols-2 gap-5"
                        data-testid="booking-form"
                    >
                        <input required value={form.customer_name} onChange={set("customer_name")} placeholder="Full name" data-testid="booking-name-input" className={inputCls} />
                        <input required value={form.phone} onChange={set("phone")} placeholder="Phone number" data-testid="booking-phone-input" className={inputCls} />
                        <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" data-testid="booking-email-input" className={inputCls} />
                        <input required value={form.vehicle} onChange={set("vehicle")} placeholder="Vehicle (e.g. 2022 BMW M340i)" data-testid="booking-vehicle-input" className={inputCls} />
                        <select
                            required
                            value={form.service}
                            onChange={(e) => setForm((f) => ({ ...f, service: e.target.value, addon: "" }))}
                            data-testid="booking-service-select"
                            className={`${inputCls} ${form.service ? "" : "text-zinc-600"}`}
                        >
                            <option value="" disabled>
                                Select service
                            </option>
                            {SERVICE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                            <option value="Something Else / Not Sure">Something Else / Not Sure</option>
                        </select>
                        <input required type="date" value={form.date} onChange={set("date")} data-testid="booking-date-input" className={inputCls} />
                        <select value={form.time_slot} onChange={set("time_slot")} data-testid="booking-timeslot-select" className={inputCls}>
                            {TIME_SLOTS.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </select>
                        <input required value={form.address} onChange={set("address")} placeholder="Service location (street address)" data-testid="booking-address-input" className={inputCls} />

                        {showUpsell && (
                            <motion.label
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                data-testid="booking-addon-upsell"
                                className="sm:col-span-2 flex items-start gap-4 rounded-xl border border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 p-5 cursor-pointer transition-colors duration-200"
                            >
                                <input
                                    type="checkbox"
                                    checked={form.addon === BUNDLE_NAME}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, addon: e.target.checked ? BUNDLE_NAME : "" }))
                                    }
                                    data-testid="booking-addon-checkbox"
                                    className="mt-1 w-4 h-4 accent-amber-500 shrink-0"
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 font-display text-lg font-bold uppercase text-white">
                                        <Package className="w-4 h-4 text-amber-400" />
                                        Add the Full Cleanse Package — $80 + parts
                                    </div>
                                    <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
                                        Injector cleaner, anti-bacterial cleanse, induction cleanse and oil flush in
                                        the same visit — about an extra hour, and you save $20 off booking them
                                        separately.
                                    </div>
                                </div>
                                <span className="shrink-0 text-[10px] font-mono uppercase tracking-[0.15em] text-amber-400 border border-amber-500/30 rounded-full px-3 py-1">
                                    Save $20
                                </span>
                            </motion.label>
                        )}

                        <textarea
                            value={form.notes}
                            onChange={set("notes")}
                            placeholder="Symptoms or notes (optional) — noises, warning lights, parking instructions..."
                            rows={4}
                            data-testid="booking-notes-input"
                            className={`${inputCls} sm:col-span-2 resize-none`}
                        />
                        {error && (
                            <div className="sm:col-span-2 text-sm text-red-400 font-mono" data-testid="booking-error">
                                {error}
                            </div>
                        )}
                        <motion.button
                            type="submit"
                            disabled={loading}
                            data-testid="booking-submit-button"
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.98 }}
                            className="sm:col-span-2 bg-amber-500 disabled:opacity-50 text-[#0B0C0E] font-bold uppercase tracking-widest text-sm rounded-xl px-8 py-4 hover:bg-amber-400 transition-colors duration-200 inline-flex items-center justify-center gap-2"
                        >
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                            {loading ? "Sending to dispatch..." : "Request Dispatch"}
                        </motion.button>
                    </motion.form>
                )}
            </div>
        </section>
    );
}
